import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Table,
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { toast } from 'react-toastify';
import Papa from 'papaparse';

import PaginationComponent from '../../Components/Common/Pagination';
import {
  requestPresignedUrl,
  uploadFileToS3,
  fetchAISummary,
  fetchUploadedFiles,
  deleteFile,
  downloadFile
} from '../../slices/pdfSummarization/thunk';
import { resetUpload, clearError, clearSummary } from '../../slices/pdfSummarization/reducer';

const MAX_CSV_ROWS_PREVIEW = 100;
const MAX_CSV_COLS_PREVIEW = 50;

const PDFTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  // Redux state
  const {
    uploadedFiles,
    totalCount,
    currentFile,
    aiSummary,
    preSignedData,
    loading,
    listLoading,
    deleteLoading,
    summaryLoading,
    error
  } = useSelector((state) => state.Pdf);

  // Local state
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const [viewModal, setViewModal] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);

  const [textContent, setTextContent] = useState(null);
  const [textLoading, setTextLoading] = useState(false);

  // CSV preview state
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);

  document.title = "Document Scanner | Verdex";

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    const offset = (currentPage - 1) * itemsPerPage;
    dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
  }, [dispatch, currentPage, itemsPerPage]);

  useEffect(() => {
    if (error && !listLoading) toast.error(error);
  }, [error, listLoading]);

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (deleteModal) setFileToDelete(null);
  };

  const toggleViewModal = () => {
    setViewModal(!viewModal);
    if (viewModal) {
      setSelectedPdf(null);
      setTextContent(null);
      setCsvHeaders([]);
      setCsvRows([]);
      dispatch(clearSummary());
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    try {
      await dispatch(deleteFile({ fileId: fileToDelete.document_id })).unwrap();
      toast.success(`${fileToDelete.file_name} deleted successfully`);

      const remainingItemsOnPage = uploadedFiles.length - 1;

      if (remainingItemsOnPage === 0 && currentPage > 1) {
        const newPage = currentPage - 1;
        setCurrentPage(newPage);
        const offset = (newPage - 1) * itemsPerPage;
        dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
      } else {
        const offset = (currentPage - 1) * itemsPerPage;
        dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
      }

      toggleDeleteModal();
    } catch (e) {
      toast.error('Failed to delete file. Please try again.');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.warning('File size must be less than 10MB');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    dispatch(resetUpload());

    try {
      toast.info(`Uploading "${file.name}"...`);

      const urlResult = await dispatch(
        requestPresignedUrl({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size
        })
      ).unwrap();

      await dispatch(
        uploadFileToS3({
          file,
          preSignedUrl: urlResult.preSignedUrl
        })
      ).unwrap();

      toast.success('File uploaded successfully! Processing...');

      const offset = (currentPage - 1) * itemsPerPage;
      dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
    } catch (err) {
      toast.error(err || 'Upload failed. Please try again.');
    }

    event.target.value = '';
  };

  const handleDownloadFile = async (fileId, fileName) => {
    if (!fileId) {
      toast.error('Invalid file ID');
      return;
    }
    window.open(fileId, '_blank', 'noopener,noreferrer');
  };

  const parseCsvForPreview = (csvText) => {
    const parsed = Papa.parse(csvText, {
      skipEmptyLines: true
    });

    const rows = Array.isArray(parsed.data) ? parsed.data : [];
    if (rows.length === 0) return { headers: [], body: [] };

    const headers = (rows[0] || []).slice(0, MAX_CSV_COLS_PREVIEW).map((h) => String(h ?? ''));
    const body = rows
      .slice(1, 1 + MAX_CSV_ROWS_PREVIEW)
      .map((r) => (Array.isArray(r) ? r : []))
      .map((r) => r.slice(0, MAX_CSV_COLS_PREVIEW).map((c) => String(c ?? '')));

    return { headers, body };
  };

  const handleViewFile = async (file) => {
    if (!file?.document_id) {
      toast.warning('No correlation ID found for this file');
      return;
    }

    setSelectedPdf(file);
    setViewModal(true);
    setTextContent(null);
    setCsvHeaders([]);
    setCsvRows([]);

    let summaryUrl = null;

    try {
      const summaryResult = await dispatch(fetchAISummary({ correlationId: file.document_id })).unwrap();
      if (summaryResult && summaryResult.summary) {
        summaryUrl = summaryResult.summary.s3_pdf_url || summaryResult.summary.file_url;
      }
    } catch (e) {
      toast.error('Failed to fetch summary');
      // Proceed even if summary fails (for CSV viewing fallback)
    }

    // Fetch raw content for text-like files (including CSV)
    // Use URL from summary if available, otherwise from the file list
    const url = summaryUrl || file.s3_pdf_url || file.file_url;
    const fileName = file.file_name || file.name || '';
    const lowerUrl = (url || '').toLowerCase();
    const lowerName = (fileName || '').toLowerCase();

    const textExts = ['.txt', '.csv', '.json', '.md', '.html', '.rtf'];
    const isTextFile = textExts.some((ext) => lowerUrl.endsWith(ext) || lowerName.endsWith(ext));

    if (isTextFile && url) {
      setTextLoading(true);
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Fetch failed');
        const text = await response.text();
        setTextContent(text);

        const isCsv = lowerUrl.endsWith('.csv') || lowerName.endsWith('.csv');
        if (isCsv) {
          const { headers, body } = parseCsvForPreview(text);
          setCsvHeaders(headers);
          setCsvRows(body);
        }
      } catch (e) {
        console.error('Error fetching text content:', e);
        // toast.error('Failed to load file content');
      } finally {
        setTextLoading(false);
      }
    }
  };

  const handleCopySummary = () => {
    const summaryText = aiSummary?.summary?.summary;
    if (summaryText) {
      navigator.clipboard.writeText(summaryText);
      toast.success('Summary copied to clipboard!');
    }
  };

  const handleExportSummary = () => {
    const summaryText = aiSummary?.summary?.summary;
    if (!summaryText) return;

    const blob = new Blob([summaryText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'ai-summary.txt');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    toast.success('Summary exported successfully!');
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'pending_upload':
        return 'Pending';
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing';
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown status';
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending_upload: 'warning',
      processing: 'info',
      completed: 'success',
      failed: 'danger',
    };
    return <Badge color={colors[status] || 'secondary'}>{getStatusMessage(status)}</Badge>;
  };

  const getPreviewInfo = () => {
    const url =
      aiSummary?.summary?.s3_pdf_url ||
      aiSummary?.summary?.file_url ||
      selectedPdf?.s3_pdf_url ||
      selectedPdf?.file_url;

    const mimeType =
      aiSummary?.summary?.content_type ||
      selectedPdf?.content_type ||
      '';

    const fileName =
      aiSummary?.summary?.file_name ||
      aiSummary?.summary?.name ||
      selectedPdf?.file_name ||
      selectedPdf?.name ||
      '';

    const urlPath = url ? url.split('?')[0].toLowerCase() : '';
    const lowerName = (fileName || '').toLowerCase();

    const officeExts = ['.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.odt', '.ods'];
    const isOffice = officeExts.some((ext) => urlPath.endsWith(ext) || lowerName.endsWith(ext));

    // Priority check: If it's an Office file, it's NOT a PDF, even if the mime type says so (incorrect mime type)
    const isPdf = !isOffice && (mimeType.includes('pdf') || urlPath.endsWith('.pdf') || lowerName.endsWith('.pdf'));

    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff', '.svg', '.heic', '.heif'];
    const isImage = mimeType.startsWith('image/') || imageExts.some((ext) => urlPath.endsWith(ext) || lowerName.endsWith(ext));

    const textExts = ['.txt', '.csv', '.json', '.md', '.html', '.rtf'];
    const isText = mimeType.startsWith('text/') || textExts.some((ext) => urlPath.endsWith(ext) || lowerName.endsWith(ext));

    const isCsv =
      mimeType === 'text/csv' ||
      urlPath.endsWith('.csv') ||
      lowerName.endsWith('.csv');

    return { url, fileName, mimeType, isPdf, isImage, isText, isCsv, isOffice };
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const offset = (pageNumber - 1) * itemsPerPage;
    dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const {
    url: previewUrl,
    isPdf,
    isImage,
    isText,
    isCsv,
    isOffice,
  } = getPreviewInfo();

  const detIStTimemp = (dateStr) => {
    const utcDate = new Date(dateStr);
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(utcDate.getTime() + istOffset);
    return istDate.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false });
  };

  const csvHasData = csvHeaders.length > 0 && csvRows.length > 0;

  return (
    <React.Fragment>
      <div>
        <Container fluid>
          <Row>
            <Col>
              <div className="d-flex gap-2 mb-3">
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleUploadClick}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <>
                      <i className="ri-upload-line me-2"></i> Upload Document
                    </>
                  )}
                </Button>

                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    const offset = (currentPage - 1) * itemsPerPage;
                    dispatch(fetchUploadedFiles({ itemsPerPage, offset }));
                  }}
                  disabled={listLoading || loading}
                >
                  <>
                    <i className="ri-refresh-line me-2"></i> Refresh
                  </>
                </Button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.json,.html,.md,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff,.svg,.heic,.heif"
                  style={{ display: 'none' }}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">List Uploaded Files</h5>
                </CardHeader>
                <CardBody>
                  {listLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading files...</p>
                    </div>
                  ) : uploadedFiles && uploadedFiles.length > 0 ? (
                    <>
                      <div className="table-responsive">
                        <Table className="table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>File Name</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadedFiles.map((file) => (
                              <tr key={file.document_id}>
                                <td>
                                  <div>
                                    <i className="ri-file-3-line text-primary me-2"></i>
                                    <span className="fw-medium">{file.name || file.file_name}</span>
                                  </div>
                                  <small className="text-muted">
                                    {detIStTimemp(file.updated_at)}
                                  </small>
                                </td>
                                <td>{getStatusBadge(file?.status)}</td>
                                <td>
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      href="#"
                                      className="btn btn-soft-secondary btn-sm dropdown"
                                      tag="button"
                                    >
                                      <i className="ri-more-fill align-middle"></i>
                                    </DropdownToggle>
                                    <DropdownMenu
                                      className="dropdown-menu-end"
                                      container="body"
                                      style={{ zIndex: 1050 }}
                                    >
                                      <DropdownItem
                                        onClick={() => handleViewFile(file)}
                                        disabled={file.status === 'processing' || file.status === 'pending_upload'}
                                      >
                                        <i className="ri-eye-line me-2"></i>
                                        View Summary
                                      </DropdownItem>

                                      <DropdownItem divider />

                                      <DropdownItem
                                        onClick={() => handleDeleteClick(file)}
                                        className="text-danger"
                                      >
                                        <i className="ri-delete-bin-line me-2"></i>
                                        Delete
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      <Row className="mt-3">
                        <Col>
                          <p className="text-muted mb-0">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                            {Math.min(currentPage * itemsPerPage, totalCount)} of{' '}
                            {totalCount} entries
                          </p>
                        </Col>
                      </Row>

                      {totalPages > 1 && (
                        <PaginationComponent
                          currentPage={currentPage}
                          totalPages={totalPages}
                          handlePageChange={handlePageChange}
                        />
                      )}
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <i className="ri-file-3-line text-muted" style={{ fontSize: '3rem' }}></i>
                      <p className="text-muted mt-3 mb-1">No files uploaded yet</p>
                      <p className="text-muted small">Upload a file to see it listed here</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* View Modal */}
      <Modal
        isOpen={viewModal}
        toggle={toggleViewModal}
        style={{ maxWidth: '90%', width: '90%' }}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={toggleViewModal}>
          <div className="d-flex align-items-center gap-2">
            <i className="ri-file-3-line"></i>
            Document Summary - {aiSummary?.summary?.file_name || aiSummary?.summary?.name || 'N/A'}
          </div>
        </ModalHeader>

        <ModalBody style={{ minHeight: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
          {summaryLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="text-muted mt-3">Loading file and summary...</p>
            </div>
          ) : (
            <Row className="g-0">
              {/* Left: Preview */}
              <Col md={6} className="pe-3 border-end">
                <div className="d-flex flex-column h-100">
                  <h6 className="mb-3 text-muted">Document Preview</h6>

                  <div
                    className="flex-grow-1 d-flex align-items-center justify-content-center bg-light rounded p-3"
                    style={{ minHeight: '500px' }}
                  >
                    {textLoading ? (
                      <div className="text-center">
                        <Spinner color="primary" />
                        <p className="text-muted mt-2">Loading file content...</p>
                      </div>
                    ) : (
                      // IMPORTANT FIX:
                      // Show TEXT/CSV preview even if previewUrl is missing.
                      isText && textContent ? (
                        <div
                          className="w-100 h-100 bg-white rounded p-3"
                          style={{
                            maxHeight: '500px',
                            overflow: 'auto',
                            border: '1px solid #dee2e6'
                          }}
                        >
                          {isCsv ? (
                            csvHasData ? (
                              <div className="table-responsive h-100">
                                <Table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                                  <thead className="table-light sticky-top">
                                    <tr>
                                      {csvHeaders.map((h, idx) => (
                                        <th
                                          key={idx}
                                          className="py-1 px-2 border-bottom"
                                          style={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                                        >
                                          {h || `Col ${idx + 1}`}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {csvRows.map((row, rIdx) => (
                                      <tr key={rIdx}>
                                        {csvHeaders.map((_, cIdx) => {
                                          const value = row?.[cIdx] ?? '';
                                          return (
                                            <td
                                              key={cIdx}
                                              className="py-1 px-2 border-bottom"
                                              style={{
                                                maxWidth: '180px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                              }}
                                              title={value}
                                            >
                                              {value}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}

                                    {Papa.parse(textContent, { skipEmptyLines: true })?.data?.length - 1 > MAX_CSV_ROWS_PREVIEW && (
                                      <tr>
                                        <td colSpan={csvHeaders.length} className="text-center text-muted py-3">
                                          Preview limited to first {MAX_CSV_ROWS_PREVIEW} rows
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <i className="ri-file-excel-2-line text-muted" style={{ fontSize: '3rem' }}></i>
                                <p className="text-muted mt-3 mb-0">CSV loaded, but no rows to display</p>
                              </div>
                            )
                          ) : (
                            <pre
                              className="mb-0"
                              style={{
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                lineHeight: '1.5'
                              }}
                            >
                              {textContent}
                            </pre>
                          )}
                        </div>
                      ) : previewUrl ? (
                        isPdf ? (
                          <embed
                            src={previewUrl}
                            type="application/pdf"
                            width="100%"
                            height="500px"
                            style={{ border: 'none', borderRadius: '4px' }}
                          />
                        ) : isImage ? (
                          <img
                            src={previewUrl}
                            alt="Preview"
                            style={{ maxHeight: '500px', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px' }}
                          />
                        ) : isOffice ? (
                          <div className="w-100 h-100" style={{ position: 'relative' }}>
                            <iframe
                              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`}
                              width="100%"
                              height="500px"
                              style={{ border: 'none', borderRadius: '4px' }}
                              title="Document Preview"
                            />
                            <div className="mt-2 text-center">
                              <small className="text-muted">Preview powered by Microsoft Office Viewer</small>
                              <Button
                                color="link"
                                size="sm"
                                onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
                              >
                                <i className="ri-external-link-line me-1"></i>
                                Open in new tab
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center">
                            <i className="ri-file-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                            <p className="text-muted mt-3">Preview not supported for this file type</p>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
                            >
                              <i className="ri-external-link-line me-1"></i>
                              Open file in new tab
                            </Button>
                          </div>
                        )
                      ) : (
                        <div className="text-center">
                          <i className="ri-file-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                          <p className="text-muted mt-3">File preview not available</p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </Col>

              {/* Right: Summary */}
              <Col md={6} className="ps-3">
                <div className="d-flex flex-column h-100">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 text-muted">AI Summary</h6>
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleCopySummary}
                        disabled={!aiSummary?.summary?.summary}
                      >
                        <i className="ri-file-copy-line me-1"></i>
                        Copy
                      </Button>
                      <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={handleExportSummary}
                        disabled={!aiSummary?.summary?.summary}
                      >
                        <i className="ri-download-line me-1"></i>
                        Export
                      </Button>
                    </div>
                  </div>

                  <div
                    className="flex-grow-1 rounded p-3 bg-light"
                    style={{ minHeight: '500px', maxHeight: '500px', overflowY: 'auto' }}
                  >
                    {aiSummary?.summary?.summary ? (
                      <pre
                        className="mb-0"
                        style={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                          fontSize: '0.9rem',
                          lineHeight: '1.6'
                        }}
                      >
                        {aiSummary.summary?.summary}
                      </pre>
                    ) : (
                      <div className="text-center py-5">
                        <i className="ri-robot-line text-muted" style={{ fontSize: '3rem' }}></i>
                        <p className="text-muted mt-3">No summary available</p>
                      </div>
                    )}
                  </div>

                  {aiSummary?.summary?.metadata && (
                    <div className="mt-3 p-2 bg-secondary bg-opacity-10 rounded">
                      <small className="text-muted">
                        <strong>Metadata:</strong> {JSON.stringify(aiSummary.summary?.metadata)}
                      </small>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </ModalBody>

        <ModalFooter />
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered backdrop="static">
        <ModalHeader toggle={toggleDeleteModal}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i className="ri-delete-bin-line text-danger" style={{ fontSize: '3.5rem' }}></i>
            <h5 className="mt-3 mb-2">Are you sure?</h5>
            <p className="text-muted mb-1">Do you want to delete this file?</p>

            {fileToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="d-flex align-items-center justify-content-center">
                  <i className="ri-file-3-line text-danger me-2"></i>
                  <strong>{fileToDelete.file_name || fileToDelete.name}</strong>
                </div>
              </div>
            )}

            <p className="text-muted mt-3 mb-0">
              <small>This action cannot be undone.</small>
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal} disabled={deleteLoading}>
            <i className="ri-close-line me-1"></i>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete} disabled={deleteLoading}>
            <i className="ri-delete-bin-line me-1"></i>
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PDFTab;

